import { useEffect, useMemo, useState } from "react";
import { getPositionSnapshot } from "./StockTradeService";
import { getTradingAccounts } from "./TradingAccountService";

export default function StockPositionList({ onManageTrades, onSelectSymbol }) {
  const [positions, setPositions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [sortKey, setSortKey] = useState("investedDesc");

  const formatNumber = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return "--";
    return Number(value).toFixed(2);
  };

  const calcCurrentPnl = (p) => {
    if (p.currentPrice !== null && p.currentPrice !== undefined && p.avgBuyPrice !== null && p.avgBuyPrice !== undefined) {
      return Number(p.currentPrice) * Number(p.quantity || 0) - Number(p.avgBuyPrice) * Number(p.quantity || 0);
    }
    if (p.unrealizedPnl !== null && p.unrealizedPnl !== undefined) return Number(p.unrealizedPnl);
    return null;
  };

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    const [data, acc] = await Promise.all([getPositionSnapshot(), getTradingAccounts()]);
    setPositions(data || []);
    setAccounts(acc || []);
  };

  const accountLabelById = useMemo(() => {
    const map = new Map();
    accounts.forEach((a) => {
      const label = `${a.brokerName} (${a.accountNumber || a.id})`;
      map.set(a.id, label);
    });
    return map;
  }, [accounts]);

  const totals = useMemo(() => {
    let qty = 0;
    let invested = 0;
    let realized = 0;
    let unrealized = 0;
    positions.forEach((p) => {
      qty += Number(p.quantity || 0);
      invested += Number(p.investedAmount || 0);
      realized += Number(p.realizedPnl || 0);
      unrealized += Number(p.unrealizedPnl || 0);
    });
    return { qty, invested, realized, unrealized, totalPnl: realized + unrealized };
  }, [positions]);

  const filteredPositions = useMemo(() => {
    let rows = [...positions];
    if (statusFilter !== "ALL") {
      rows = rows.filter((p) => (p.status || "").toUpperCase() === statusFilter);
    }
    switch (sortKey) {
      case "investedAsc":
        rows.sort((a, b) => Number(a.investedAmount || 0) - Number(b.investedAmount || 0));
        break;
      case "investedDesc":
        rows.sort((a, b) => Number(b.investedAmount || 0) - Number(a.investedAmount || 0));
        break;
      case "pnlAsc":
        rows.sort((a, b) => Number(a.profitLossAmount || 0) - Number(b.profitLossAmount || 0));
        break;
      case "pnlDesc":
        rows.sort((a, b) => Number(b.profitLossAmount || 0) - Number(a.profitLossAmount || 0));
        break;
      case "qtyAsc":
        rows.sort((a, b) => Number(a.quantity || 0) - Number(b.quantity || 0));
        break;
      case "qtyDesc":
        rows.sort((a, b) => Number(b.quantity || 0) - Number(a.quantity || 0));
        break;
      case "symbolAsc":
        rows.sort((a, b) => (a.symbol || "").localeCompare(b.symbol || ""));
        break;
      case "symbolDesc":
        rows.sort((a, b) => (b.symbol || "").localeCompare(a.symbol || ""));
        break;
      default:
        break;
    }
    return rows;
  }, [positions, statusFilter, sortKey]);

  return (
    <div className="card">
      <div className="stocks-toolbar">
        <div className="stocks-summary">
          <span className="stocks-badge">Total Qty: {totals.qty}</span>
          <span className="stocks-badge">Invested: {totals.invested}</span>
          <span className={`stocks-badge ${totals.realized >= 0 ? "stocks-good" : "stocks-bad"}`}>
            Realized PnL: {totals.realized}
          </span>
          <span className={`stocks-badge ${totals.unrealized >= 0 ? "stocks-good" : "stocks-bad"}`}>
            Unrealized PnL: {totals.unrealized}
          </span>
          <span className={`stocks-badge ${totals.totalPnl >= 0 ? "stocks-good" : "stocks-bad"}`}>
            Total PnL: {totals.totalPnl}
          </span>
        </div>
        <div className="row-actions">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="ALL">All Status</option>
            <option value="OPEN">Open</option>
            <option value="PARTIAL">Partial</option>
            <option value="CLOSED">Closed</option>
          </select>
          <select value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
            <option value="investedDesc">Invested High → Low</option>
            <option value="investedAsc">Invested Low → High</option>
            <option value="pnlDesc">P/L High → Low</option>
            <option value="pnlAsc">P/L Low → High</option>
            <option value="qtyDesc">Qty High → Low</option>
            <option value="qtyAsc">Qty Low → High</option>
            <option value="symbolAsc">Symbol A → Z</option>
            <option value="symbolDesc">Symbol Z → A</option>
          </select>
        </div>
      </div>
      <div className="data-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Account</th>
              <th>Qty</th>
              <th>Avg Buy</th>
              <th>Current Price</th>
              <th>Invested</th>
              <th>Profit/Loss</th>
              <th>Profit %</th>
              <th>Current P/L</th>
              <th>Realized PnL</th>
              <th>Unrealized PnL</th>
              <th>Total PnL</th>
              <th>Status</th>
              <th>Last Buy</th>
              <th>Days Since Buy</th>
              <th>Last Sell</th>
              <th>Days Since Sell</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPositions.map((p) => (
              <tr key={`${p.tradingAccountId}-${p.symbol}`}>
                <td>
                  {onSelectSymbol ? (
                    <button className="btn btn-ghost btn-sm" onClick={() => onSelectSymbol(p.symbol, p.stockName)}>
                      {p.symbol}
                    </button>
                  ) : (
                    p.symbol
                  )}
                </td>
                <td>{accountLabelById.get(p.tradingAccountId) || p.tradingAccountId}</td>
                <td>{p.quantity}</td>
                <td>{p.avgBuyPrice}</td>
                <td>{formatNumber(p.currentPrice)}</td>
                <td>{p.investedAmount}</td>
                <td>{p.profitLossAmount}</td>
                <td>{p.profitLossPercent}</td>
                <td>
                  {(() => {
                    const pnl = calcCurrentPnl(p);
                    if (pnl === null) return "--";
                    return (
                      <span className={`stocks-badge ${pnl >= 0 ? "stocks-good" : "stocks-bad"}`}>
                        {formatNumber(pnl)}
                      </span>
                    );
                  })()}
                </td>
                <td>{p.realizedPnl}</td>
                <td>{p.unrealizedPnl}</td>
                <td>{Number(p.realizedPnl || 0) + Number(p.unrealizedPnl || 0)}</td>
                <td>
                  <span className={`stocks-pill stocks-pill-${(p.status || "").toLowerCase()}`}>
                    {p.status}
                  </span>
                </td>
                <td>{p.lastBuyDate}</td>
                <td>{p.daysSinceLastBuy ?? ""}</td>
                <td>{p.lastSellDate}</td>
                <td>{p.daysSinceLastSell ?? ""}</td>
                <td>
                  <button
                    className="btn btn-secondary"
                    onClick={() => onManageTrades && onManageTrades(p.tradingAccountId, p.symbol)}
                  >
                    Edit/Delete Trades
                  </button>
                </td>
              </tr>
            ))}
            {filteredPositions.length === 0 && (
              <tr>
                <td colSpan="15">No positions found.</td>
              </tr>
            )}
          </tbody>
          {filteredPositions.length > 0 && (
            <tfoot>
              <tr className="stocks-totals-row">
                <td colSpan="2">Totals</td>
                <td>{totals.qty}</td>
                <td></td>
                <td></td>
                <td>{totals.invested}</td>
                <td></td>
                <td></td>
                <td></td>
                <td>{totals.realized}</td>
                <td>{totals.unrealized}</td>
                <td>{totals.totalPnl}</td>
                <td colSpan="6"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
